import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Tracks whether any FooterForm is currently mounted AND visible, so layout
// components (e.g. ColumnLayout) can reserve space for the fixed action bar
// without callers passing props through the tree.
//
// Refcount-based to support concurrent FooterForms (rare but possible across
// modals/drawers). The default value is a no-op so components remain usable
// outside the Provider (e.g. unit tests).

const noop = () => () => {};

const FooterFormPresenceContext = createContext({
  isVisible: false,
  register: noop,
});

export const FooterFormPresenceProvider = ({ children }) => {
  const [count, setCount] = useState(0);

  const register = useCallback(() => {
    setCount(c => c + 1);
    return () => setCount(c => c - 1);
  }, []);

  const value = useMemo(() => ({ isVisible: count > 0, register }), [count, register]);

  return <FooterFormPresenceContext.Provider value={value}>{children}</FooterFormPresenceContext.Provider>;
};

FooterFormPresenceProvider.propTypes = {
  children: PropTypes.node,
};

export const useIsFooterFormVisible = () => useContext(FooterFormPresenceContext).isVisible;

export const useRegisterFooterFormPresence = visible => {
  const { register } = useContext(FooterFormPresenceContext);
  useEffect(() => {
    if (!visible) return undefined;
    return register();
  }, [visible, register]);
};

export default FooterFormPresenceContext;
